
import { getUberShader } from "./UberShader";
import { getBasicShader } from "./BasicShader";
import { DepthFormat, invertDepthCompare } from "../CommonRenderTargets";
import { getBufferLayout, getPipelineHash, isValidWideLineGeometry } from "../Pipelines";
import { sideToCullMode } from "../compat";
import { initMaterialBindings, MaterialUniformFlags } from "./MaterialUniforms";
import { ToneMapMethod } from "../Renderer";
import { getDepthBiasValues } from "./DepthBiasMap";

/** @import { VertexBuffer } from "../VertexBuffer" */
/** @import { PointDataBuffer } from "../PointDataBuffer" */
/** @import { MultiDrawBatch } from "../gpudrivenrender/MultiDrawBatch" */

const CustomTextureBindGroup = 3;

export class UberPipeline {

  #renderer;
  /** @type {GPUDevice} */
  #device;

  #pipelines = new Map();
  /** @type {GPUBindGroupLayout[]} */
  #activeBindGroupLayout = [];
  /** @type {GPUColorTargetState[]} */
  #activeTargetsList = [];
  /** @type {VertexBuffer} */
  #vb;
  /** @type {PointDataBuffer} */
  #pointDataBuffer;
  /** @type {GPUBindGroup} */
  #defaultMaterialBindGroup;
  /** @type {GPUBindGroupLayout|undefined} */
  #pointDataBindGroupLayout;

  /**
   * @param {Renderer} renderer
   * @param {GPUBindGroup} defaultMaterialBindGroup - Default bind group for materials without textures
   */
  constructor(renderer, defaultMaterialBindGroup) {
    this.#renderer = renderer;
    this.#device = renderer.getDevice();
    this.#vb = this.#renderer.getVB();
    this.#pointDataBuffer = this.#renderer.getPointDataBuffer();
    this.#defaultMaterialBindGroup = defaultMaterialBindGroup;
  }

  #createPipeline(key, geometry, material, materialTextureMask, hasInstanceIdMap, useWboit, idOnly = false) {
    const attributes = geometry.attributes;
    const includeVC = !!(attributes.color && material.vertexColors);
    const isUVW = !!attributes.uvw;
    const hasUV = !!attributes.uv || isUVW;
    const hasTextures = hasUV && materialTextureMask !== 0;
    const hasWideLines = material.wideLines && isValidWideLineGeometry(geometry);
    // Skip normals for THREE.BasicMaterial which signals that rendering without lighting is desired (e.g. gizmos).
    const isUnorm16 = geometry.hasUnorm16Layout?.();
    const includeNormals = (!!attributes.normal || isUnorm16) && !(material instanceof THREE.MeshBasicMaterial);

    let pipeline;

    const depthCompare = idOnly || material.depthTest ? invertDepthCompare(material.depthFunc || 'less-equal') : 'always';
    const categoryHighlightingEnabled = this.#renderer.isCategoryHighlightingEnabled();
    const categoryHighlightingFallback = this.#renderer.isCategoryHighlightingFallback();
    if (geometry.isLines) {

      let shader = this.#device.createShaderModule({
        label: 'basic shader 3D lines',
        code: getBasicShader(includeVC, hasWideLines, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, false, false, isUnorm16)
      });

      const sampleCount = this.#renderer.getMSAASampleCount();
      pipeline = this.#device.createRenderPipeline({
        label: 'uber pipeline 3D lines',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts: this.#activeBindGroupLayout
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, false, false, includeVC, false, false)
        },
        fragment: {
          module: shader,
          entryPoint: 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'line-list',
          cullMode: 'none'
        },
        depthStencil: {
          depthWriteEnabled: useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat
        },
        multisample: {
          count: sampleCount
        }
      });
    } else if (geometry.isPoints) {
      // Determine point data layout from geometry attributes
      const colorAttr = attributes.color;
      const pointStride = geometry.vbstride;
      const colorOffset = colorAttr?.offset ?? 3;
      const isByteColor = colorAttr?.bytesPerItem === 1;
      // Points generate UV from quad corners — no geometry uv attr required.
      const pointsHasTextures = materialTextureMask !== 0;

      let shader = this.#device.createShaderModule({
        label: 'basic shader points',
        code: getBasicShader(includeVC, false, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, false, pointsHasTextures, false, true, pointStride, colorOffset, isByteColor)
      });

      const pointsBindGroupLayouts = [...this.#activeBindGroupLayout, this.#pointDataBindGroupLayout];

      const sampleCount = this.#renderer.getMSAASampleCount();
      pipeline = this.#device.createRenderPipeline({
        label: 'uber pipeline points',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts: pointsBindGroupLayouts
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, false, false, includeVC, false, false, true)
        },
        fragment: {
          module: shader,
          entryPoint: 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'triangle-list',
          cullMode: 'none'
        },
        depthStencil: {
          depthWriteEnabled: useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat
        },
        multisample: {
          count: sampleCount
        }
      });
    } else {

      // Query per-material custom sampled texture (e.g. VirtualTexture) for its WGSL chunk.
      // Only the diffuse slot is currently supported. The framework owns the applicability
      // gate (#usesCustomTexture); getShaderChunk() is only called when it applies and is
      // contracted to always return a non-empty chunk.
      const customTex = material.customSampledTextures?.diffuse;
      const hasCustomTexture = UberPipeline.#usesCustomTexture(geometry, material);
      const customTexShaderChunk = hasCustomTexture ? customTex.getShaderChunk({ isUVW, includeNormals }) : '';

      let shader;

      if (includeNormals) {

        const { isGamma, isRGBM } = this.#renderer.getEnvMapEncoding();

        // Activate gamma-decoding for input colors if tone-mapping is used
        // (e.g., see inputToLinear() in uber.wgsl)
        const gammaInput = this.#renderer.getTonemapMethod() != ToneMapMethod.None;
        const useIrradianceMap = this.#renderer.getIBL().hasIrradianceMap();

        shader = this.#device.createShaderModule({
          label: 'uber shader triangles (' + key + ')',
          code: getUberShader(material, hasUV, hasTextures, includeVC, isUVW, isGamma, isRGBM, gammaInput,
          hasInstanceIdMap, useIrradianceMap, useWboit, categoryHighlightingEnabled, categoryHighlightingFallback,
          idOnly, isUnorm16, customTexShaderChunk)
        });
      } else {
        // Fallback for shapes without normals (e.g. gizmos with BasicMaterial)
        shader = this.#device.createShaderModule({
          label: 'basic shader triangles (' + key + ')',
          code: getBasicShader(includeVC, hasWideLines, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, hasUV, hasTextures, isUnorm16)
        });
      }

      // "depthBias, depthBiasSlopeScale, and depthBiasClamp have no effect on 'point-list', 'line-list', and
      // 'line-strip' primitives, and must be 0." ...from https://www.w3.org/TR/webgpu/#depth-stencil-state
      const { depthBias, depthBiasSlopeScale } = getDepthBiasValues(material);

      // Add custom-sampled-texture bind group layout when applicable. Slots 0-2 (frame,
      // render batch, material) always exist, so CustomTextureBindGroup (3) is the next
      // free slot.
      let bindGroupLayouts = this.#activeBindGroupLayout;
      if (hasCustomTexture) {
        bindGroupLayouts = [...this.#activeBindGroupLayout];
        bindGroupLayouts[CustomTextureBindGroup] = customTex.getBindGroupLayout();
      }

      const sampleCountTris = this.#renderer.getMSAASampleCount();
      const pipelineDesc = {
        label: 'uber pipeline triangles (' + key + ')',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, includeNormals, hasUV, includeVC, isUVW, hasWideLines)
        },
        fragment: {
          module: shader,
          entryPoint: idOnly ? 'idOnlyPsMain' : 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'triangle-list',
          cullMode: sideToCullMode(material.side)
        },

        depthStencil: {
          depthWriteEnabled: idOnly ? true : useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat,
          depthBias,
          depthBiasSlopeScale
        },
        multisample: {
          count: sampleCountTris
        }
      };
      pipeline = this.#device.createRenderPipeline(pipelineDesc);
    }

    this.#pipelines.set(key, pipeline);

    return pipeline;
  }

  resetPipelines() {
    this.#pipelines.clear();
  }

  /**
   * @param {GPUBindGroupLayout[]} layouts
   * @param {GPUColorTargetState[]} targets
   */
  reset(layouts, targets, pointDataBindGroupLayout) {
    this.#activeBindGroupLayout = layouts;
    this.#activeTargetsList = targets;
    this.#pointDataBindGroupLayout = pointDataBindGroupLayout;
  }

  #activateMaterialBindings(passEncoder, material) {

    let materialUniformsMask = material.__gpuUniformsMask | 0;

    if (materialUniformsMask === MaterialUniformFlags.NO_UNIFORMS && !material.needsUpdate) {
      // Reset to default placeholder to avoid stale bindGroup from previous textured material
      passEncoder.setBindGroupIfNeeded(2, this.#defaultMaterialBindGroup);
      return 0;
    }

    if (materialUniformsMask === 0 || material.needsUpdate) {
      materialUniformsMask = initMaterialBindings(this.#device, material, this.#renderer.getPlaceholderTexture());
    }

    if (materialUniformsMask === MaterialUniformFlags.NO_UNIFORMS) {
      // Reset to default placeholder to avoid stale bindGroup from previous textured material
      passEncoder.setBindGroupIfNeeded(2, this.#defaultMaterialBindGroup);
      return 0;
    }

    passEncoder.setBindGroupIfNeeded(2, material.__gpuMaterialUniforms.getBindGroup());

    return materialUniformsMask;
  }

  /**
   * Unified check to decide whether a custom sampled texture (e.g. VirtualTexture) is
   * applied to a given (geometry, material). Custom textures need UVs to sample and are only
   * injected into the lit triangle uber shader, so they apply only to UV-bearing triangle
   * geometry rendered with normals. Used identically for the pipeline cache key, the pipeline
   * bind-group layout, and the draw-time bind-group, so the three can never diverge.
   *
   * TODO: Extend to the unlit basic shader (getBasicShader / basic.wgsl) so custom textures
   * also work on MeshBasicMaterial and normal-less geometry. Requires mirroring the uber.wgsl
   * injection points in basic.wgsl (which is shared with lines/points and has no uvw path).
   *
   * @param {BufferGeometry} geometry
   * @param {Material} material
   * @returns {boolean}
   */
  static #usesCustomTexture(geometry, material) {
    if (!material.customSampledTextures?.diffuse) {
      return false;
    }
    const attributes = geometry.attributes;
    const hasUV = !!attributes.uv || !!attributes.uvw;
    // Only the lit triangle path (getUberShader) injects the custom-texture chunk; the
    // unlit basic shader path does not (yet) — see TODO above.
    const includeNormals = (!!attributes.normal || geometry.hasUnorm16Layout?.()) && !(material instanceof THREE.MeshBasicMaterial);
    return hasUV && includeNormals && !geometry.isLines && !geometry.isPoints;
  }

  #preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, useWboit, idOnly = false) {
    const materialUniformsMask = this.#activateMaterialBindings(passEncoder, material);
    const materialTextureMask = materialUniformsMask & MaterialUniformFlags.TEXTURE_MASK;

    const attributes = geometry.attributes;
    const includeVC = attributes.color && material.vertexColors;
    const hasUV = attributes.uv || attributes.uvw;
    const hasTextures = (hasUV || geometry.isPoints) && materialTextureMask !== 0;
    const hasWideLines = Boolean(material.wideLines) && isValidWideLineGeometry(geometry);
    const hasCustomTexture = UberPipeline.#usesCustomTexture(geometry, material);
    const key = getPipelineHash(geometry, material, hasUV, includeVC, hasTextures, hasWideLines, hasInstanceIdMap, useWboit, idOnly, hasCustomTexture);

    let pipeline = this.#pipelines.get(key);
    if (!pipeline) {
      pipeline = this.#createPipeline(key, geometry, material, materialTextureMask, hasInstanceIdMap, useWboit, idOnly);
    }

    passEncoder.setPipelineIfNeeded(pipeline);

    // Bind the per-material custom sampled texture (group 3) when applicable.
    if (hasCustomTexture) {
      passEncoder.setBindGroupIfNeeded(CustomTextureBindGroup, material.customSampledTextures.diffuse.getBindGroup());
    }

    return materialTextureMask;
  }

  drawOne(passEncoder, objectIndex, geometry, material, useWboit, idOnly = false) {
    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, false, useWboit, idOnly);

    if (geometry.isPoints) {
      // Ensure point SSBO is uploaded before creating the bind group
      this.#pointDataBuffer.ensurePointData(geometry);
      // Set the point data storage buffer bind group
      const bindGroup = this.#getOrCreatePointDataBindGroup(geometry);
      if (!bindGroup) {
        return materialTextureMask;
      }
      passEncoder.setBindGroupIfNeeded(3, bindGroup);
      this.#pointDataBuffer.drawPoints(passEncoder, geometry, objectIndex);
    } else {
      this.#vb.draw(passEncoder, geometry, objectIndex);
    }
    return materialTextureMask;
  }

  /**
   * Get or create a bind group for the point data storage buffer.
   * Cached on the geometry object; invalidated when the SSBO is destroyed.
   * @param {BufferGeometry} geometry
   * @returns {GPUBindGroup|null}
   */
  #getOrCreatePointDataBindGroup(geometry) {
    if (geometry.__gpuPointDataBindGroup) {
      return geometry.__gpuPointDataBindGroup;
    }

    const buffer = geometry.__gpuPointSSBO;
    if (!buffer) {
      console.error('Point geometry missing storage buffer');
      return null;
    }

    geometry.__gpuPointDataBindGroup = this.#device.createBindGroup({
      label: 'point data bind group',
      layout: this.#pointDataBindGroupLayout,
      entries: [{
        binding: 0,
        resource: {
          buffer,
          offset: geometry.__gpuPointSSBOOffset,
          size: geometry.__gpuPointSSBOSize
        }
      }]
    });

    return geometry.__gpuPointDataBindGroup;
  }

  /**
   * @param {GPUCommandEncoder} passEncoder
   * @param {MultiDrawCall} multiDrawCall
   * @param {MultiDrawBatch} mdBatch
   */
  drawMulti(passEncoder, multiDrawCall, mdBatch) {
    // Why is it okay to use just the first geometry/material as reference for the whole batch?
    // The code that creates the MultiDrawBatch (see MultiDrawRecorder.#needsNewMultiDraw) is
    // responsible for ensuring all members share the same pipeline-relevant state — pipeline
    // hash, vertex/index buffers, and custom sampled texture.
    const geometry = multiDrawCall.firstGeometry;
    const material = multiDrawCall.firstMaterial;
    const hasInstanceIdMap = mdBatch.enableCulling;
    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, false);

    this.#vb.drawMulti(passEncoder, multiDrawCall, mdBatch);

    return materialTextureMask;
  }
}